// Pending pulls: after a CONFIRMED sealed open, "log what you pulled" is
// owed until a copies add from that prompt is confirmed or the user
// dismisses it (binders spec 5.3: two writes, two op_keys; a crash between
// them must not lose the pulls). Collection-scoped and persisted in
// localStorage, so it survives an unmount and a reload; kept per (user,
// collection) like the retry intents (copiesRetryIntents.ts), and never shown
// to another user.
import { ownerStorage } from './offline/ownerStorage'
import { useSyncExternalStore } from 'react'

export interface PendingPull {
  id: string
  /** The product opened, as shown. */
  name: string
  /** Units opened. */
  opened: number
  /** ms since epoch. */
  at: number
}

const PREFIX = 'cb_sealed_pending_pulls'
const MAX = 20
const NONE: readonly PendingPull[] = []
const listeners = new Set<() => void>()
const cache = new Map<string, readonly PendingPull[]>()

function storageKey(userId: string, collectionId: string): string {
  return `${PREFIX}:${userId}:${collectionId}`
}

function read(key: string): readonly PendingPull[] {
  const hit = cache.get(key)
  if (hit) return hit
  let list: readonly PendingPull[] = NONE
  try {
    const raw = ownerStorage.getItem(key)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    if (Array.isArray(parsed)) {
      list = parsed.filter((p): p is PendingPull => !!p && typeof p.id === 'string' && typeof p.name === 'string'
        && typeof p.opened === 'number' && typeof p.at === 'number')
    }
  } catch { /* unreadable: none */ }
  cache.set(key, list)
  return list
}

function write(key: string, list: readonly PendingPull[]): void {
  cache.set(key, list)
  try {
    if (list.length) ownerStorage.setItemUnconfirmed(key, JSON.stringify(list))
    else ownerStorage.removeItemUnconfirmed(key)
  } catch { /* private mode: kept in memory for this tab */ }
  for (const l of listeners) l()
}

export function peekPendingPulls(userId: string | null, collectionId: string | null): readonly PendingPull[] {
  if (!userId || !collectionId) return NONE
  return read(storageKey(userId, collectionId))
}

export function addPendingPull(userId: string, collectionId: string, pull: Omit<PendingPull, 'id' | 'at'>): PendingPull {
  const key = storageKey(userId, collectionId)
  const entry: PendingPull = { ...pull, id: `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`, at: Date.now() }
  const list = [...read(key), entry]
  write(key, list.length > MAX ? list.slice(list.length - MAX) : list)
  return entry
}

/** A confirmed add from its prompt, or an explicit dismiss. */
export function clearPendingPull(userId: string, collectionId: string, id: string): void {
  const key = storageKey(userId, collectionId)
  const list = read(key)
  if (list.some(p => p.id === id)) write(key, list.filter(p => p.id !== id))
}

/** Remove of the collection, sign-out: every entry of the pair. */
export function clearAllPendingPulls(userId: string, collectionId: string): void {
  write(storageKey(userId, collectionId), NONE)
}

export function usePendingPulls(userId: string | null, collectionId: string | null): readonly PendingPull[] {
  return useSyncExternalStore(
    cb => {
      listeners.add(cb)
      // Another tab wrote: drop the cache so the next read sees it.
      const onStorage = (e: StorageEvent) => { if (e.key?.startsWith(PREFIX)) { cache.delete(e.key); cb() } }
      // eslint-disable-next-line i18next/no-literal-string -- DOM event name
      window.addEventListener('storage', onStorage)
      return () => {
        listeners.delete(cb)
        // eslint-disable-next-line i18next/no-literal-string -- DOM event name
        window.removeEventListener('storage', onStorage)
      }
    },
    () => peekPendingPulls(userId, collectionId),
    () => NONE,
  )
}

/** Account deletion: every pending pull of the user, in every collection. */
export function clearPendingPullsForUser(userId: string): void {
  const prefix = `${PREFIX}:${userId}:`
  try {
    for (const k of ownerStorage.keys()) {
      if (k?.startsWith(prefix)) ownerStorage.removeItemUnconfirmed(k)
    }
  } catch { /* private mode */ }
  for (const k of [...cache.keys()]) if (k.startsWith(prefix)) cache.delete(k)
  for (const l of listeners) l()
}

/** Test seam. */
export function __resetPendingPulls(): void {
  cache.clear()
}
